import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	FileIcon,
	Input,
	Label,
	RadioGroup,
	RadioGroupItem,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { formatSimpleDate } from "@dune/utils";
import { Download, FileSignature, Mail } from "lucide-react";
import React, { useId } from "react";
import { getPrivateFileUrl } from "../../../../api/queries/files.queries";
import { S3FileInput } from "../../../../component/common/S3FileInput";
import { useSendDocumentFlow } from "./useSendDocumentFlow";
import {
	ProjectDocumentForSignatureTypeKeys,
	SignatureMethodValues,
} from "@dune/types/src";
import { toast } from "sonner";

interface SendDocumentModalProps {
	type: ProjectDocumentForSignatureTypeKeys;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	propositionId: number;
	onSuccess?: () => void;
}

const SendDocumentModal: React.FC<SendDocumentModalProps> = ({
	type,
	open,
	onOpenChange,
	propositionId,
	onSuccess,
}) => {
	const signatureRadioId = useId();

	const {
		selectedFileId,
		signatureMethod,
		step,
		selectOpen,
		signatoryLastname,
		signatoryFirstname,
		signatoryEmail,
		signatoryPhone,
		setSelectOpen,
		setSignatureMethod,
		setSignatoryLastname,
		setSignatoryFirstname,
		setSignatoryEmail,
		setSignatoryPhone,
		documents,
		selectedDocument,
		conditionsGenerales,
		isReady,
		isLoading,
		isLoadingFiles,
		handleNext,
		handleSendDocument,
		handleFileSelection,
		handleInlineUpload,
		labels,
	} = useSendDocumentFlow({
		type,
		open,
		onOpenChange,
		propositionId,
		onSuccess,
	});
	console.log("in modal ", step);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className={step === "ELECTRONIC" ? "sm:max-w-[400px]" : "sm:max-w-4xl"}
			>
				<DialogHeader>
					<DialogTitle>
						{step === "CHOICE" && labels.title.choice}
						{step === "MANUAL" && labels.title.manual}
						{step === "ELECTRONIC" && labels.title.electronic}
					</DialogTitle>
					<DialogDescription>
						{step === "CHOICE" && labels.description.choice}
						{step === "MANUAL" && labels.description.manual}
						{step === "ELECTRONIC" && labels.description.electronic}
					</DialogDescription>
				</DialogHeader>

				{step === "CHOICE" && (
					<div className="space-y-4 py-2">
						<div className="space-y-2">
							<label htmlFor="document-select" className="text-sm font-medium">
								{labels.selectLabel}
							</label>
							{isLoadingFiles ? (
								<div className="p-2 text-sm text-gray-500">
									{labels.selectPlaceholder.loading}
								</div>
							) : documents.length === 0 ? (
								<div className="p-3 text-sm text-gray-500 bg-gray-50 rounded-md">
									{labels.selectPlaceholder.noDocuments}
								</div>
							) : (
								<Select
									value={selectedFileId}
									onValueChange={handleFileSelection}
									disabled={isLoading}
									open={selectOpen}
									onOpenChange={setSelectOpen}
								>
									<SelectTrigger id="document-select">
										<SelectValue>
											{selectedDocument && (
												<div className="flex items-center gap-2">
													<FileIcon
														mimeType={selectedDocument.mimeType}
														className="w-4 h-4"
													/>
													<span>{selectedDocument.fileName}</span>
												</div>
											)}
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										{documents.map((document) => (
											<SelectItem key={document.fileId} value={document.fileId}>
												<div className="flex items-center gap-2">
													<FileIcon
														mimeType={document.mimeType}
														className="w-4 h-4"
													/>
													<div>
														<div className="font-medium">
															{document.fileName}
														</div>
														<div className="text-xs text-gray-500">
															{formatSimpleDate(document.createdAt)}
														</div>
													</div>
												</div>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						</div>

						{isReady && (
							<div className="border-t pt-4">
								<fieldset className="space-y-2">
									<legend className="text-sm font-medium">
										Mode de signature
									</legend>
									<RadioGroup
										value={signatureMethod}
										onValueChange={(value) =>
											setSignatureMethod(value as SignatureMethodValues)
										}
										className="grid grid-cols-2 gap-3"
									>
										<Label
											htmlFor={`${signatureRadioId}-ELECTRONIC`}
											className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
										>
											<RadioGroupItem
												id={`${signatureRadioId}-ELECTRONIC`}
												value="ELECTRONIC"
												className="absolute top-2 right-2"
											/>
											<Mail className="h-8 w-8 mb-1 text-blue-600" />
											<span className="font-medium text-center">
												Signature électronique
											</span>
											<span className="text-xs text-muted-foreground text-center">
												Envoi par email avec DocuSign
											</span>
										</Label>
										<Label
											htmlFor={`${signatureRadioId}-MANUAL`}
											className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
										>
											<RadioGroupItem
												id={`${signatureRadioId}-MANUAL`}
												value="MANUAL"
												className="absolute top-2 right-2"
											/>
											<FileSignature className="h-8 w-8 mb-1 text-green-600" />
											<span className="font-medium text-center">
												Signature manuelle
											</span>
											<span className="text-xs text-muted-foreground text-center">
												Importer les documents signés
											</span>
										</Label>
									</RadioGroup>
								</fieldset>
							</div>
						)}
					</div>
				)}

				{step === "MANUAL" && (
					<div className="space-y-4">
						<div className="space-y-1.5">
							<div className="text-sm font-medium">
								{labels.primaryUploadLabel}
							</div>
							<S3FileInput
								key={`upload-primary-${open}`}
								bucket="private"
								onUploadSuccess={async (file) => {
									await handleInlineUpload(file, labels.primaryDocumentType);
								}}
								subText="Formats acceptés : PDF, DOCX, PNG, JPG (max. 10MB)"
								hidePreviewButton={true}
							/>
						</div>
						<div className="space-y-1.5">
							<div className="text-sm font-medium">
								{labels.secondaryUploadLabel}
							</div>
							<S3FileInput
								key={`upload-secondary-${open}`}
								bucket="private"
								onUploadSuccess={async (file) => {
									if (!labels.secondaryDocumentType) {
										toast.error(
											"Type de document secondaire non défini dans la config",
										);
										console.warn("No secondary document type provided");
										return;
									}
									await handleInlineUpload(file, labels.secondaryDocumentType);
								}}
								subText="Formats acceptés : PDF, DOCX, PNG, JPG (max. 10MB)"
								hidePreviewButton={true}
							/>
						</div>
						<div className="space-y-2">
							<div className="text-sm font-medium">Documents à signer</div>
							<div className="rounded-md border p-3 flex items-center justify-between">
								<div className="flex items-center gap-3">
									<FileIcon
										mimeType={selectedDocument?.mimeType || "application/pdf"}
										className="w-6 h-6"
									/>
									<div>
										<div className="text-sm font-medium">
											{labels.primaryDownloadLabel}
										</div>
										<div className="text-xs text-gray-500">
											{formatSimpleDate(
												selectedDocument?.createdAt || new Date().toISOString(),
											)}
										</div>
									</div>
								</div>
								{selectedDocument?.key && (
									<button
										type="button"
										onClick={async () => {
											const url = await getPrivateFileUrl(selectedDocument.key);
											const a = document.createElement("a");
											a.href = url;
											a.download = selectedDocument.fileName;
											a.click();
										}}
										className="p-2 rounded hover:bg-gray-100"
										title="Télécharger"
									>
										<Download className="w-5 h-5" />
									</button>
								)}
							</div>
							<div className="rounded-md border p-3 flex items-center justify-between">
								<div className="flex items-center gap-3">
									<FileIcon mimeType="application/pdf" className="w-6 h-6" />
									<div>
										<div className="text-sm font-medium">
											{labels.secondaryDownloadLabel}
										</div>
										<div className="text-xs text-gray-500">jj/mm/aaaa</div>
									</div>
								</div>
								{conditionsGenerales?.key && (
									<button
										type="button"
										onClick={async () => {
											const url = await getPrivateFileUrl(
												conditionsGenerales.key,
											);
											const a = document.createElement("a");
											a.href = url;
											a.download = conditionsGenerales.fileName;
											a.click();
										}}
										className="p-2 rounded hover:bg-gray-100"
										title="Télécharger"
									>
										<Download className="w-5 h-5" />
									</button>
								)}
							</div>
						</div>
					</div>
				)}

				{step === "ELECTRONIC" && (
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<div className="text-sm">Nom</div>
								<Input
									value={signatoryLastname}
									onChange={(e) => setSignatoryLastname(e.target.value)}
								/>
							</div>
							<div>
								<div className="text-sm">Prénom</div>
								<Input
									value={signatoryFirstname}
									onChange={(e) => setSignatoryFirstname(e.target.value)}
								/>
							</div>
						</div>
						<div>
							<div className="text-sm">Email</div>
							<Input
								type="email"
								value={signatoryEmail}
								onChange={(e) => setSignatoryEmail(e.target.value)}
							/>
						</div>
						<div>
							<div className="text-sm">Numéro de téléphone</div>
							<Input
								value={signatoryPhone}
								onChange={(e) => setSignatoryPhone(e.target.value)}
							/>
						</div>
						<p className="text-sm text-[#144c65]">{labels.docusignNotice}</p>
					</div>
				)}

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isLoading}
					>
						Annuler
					</Button>
					{step === "CHOICE" && (
						<Button onClick={handleNext} disabled={!isReady || isLoading}>
							Suivant
						</Button>
					)}
					{step !== "CHOICE" && (
						<Button
							onClick={handleSendDocument}
							disabled={isLoading}
							isLoading={isLoading}
						>
							{signatureMethod === "MANUAL"
								? labels.submitButton.manual
								: labels.submitButton.electronic}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default SendDocumentModal;
